import { useEffect, useState } from 'react';
import AgentSidebar from '../components/AgentSidebar';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const PERK_OPTIONS = ['Visa Sponsorship Included', 'Accommodation Assistance', 'Guaranteed Internship', 'Medical Care Included'];

const STATUS_STYLES = {
  OPEN: 'bg-emerald-50 text-emerald-700',
  PENDING_APPROVAL: 'bg-amber-50 text-amber-700',
  CLOSED: 'bg-[#e0e3e5] text-[#44474f]',
  DRAFT: 'bg-[#e0e3e5] text-[#44474f]',
};

const emptyForm = { title: '', country: '', type: 'STUDY', feeFromAmount: '', deadline: '', description: '', perks: [] };

export default function AgentOpeningsPage() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  const fetchOpportunities = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/opportunities`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => setOpportunities(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchOpportunities, []);

  useEffect(() => {
    if (!coverImage) return;
    const url = URL.createObjectURL(coverImage);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverImage]);

  const togglePerk = (perk) => {
    setForm((f) => ({ ...f, perks: f.perks.includes(perk) ? f.perks.filter((p) => p !== perk) : [...f.perks, perk] }));
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setCoverImage(null); setCoverPreview(null); setDrawerOpen(true); };
  const openEdit = (op) => {
    setEditingId(op.id);
    setForm({ title: op.title || '', country: op.country || '', type: op.type || 'STUDY', feeFromAmount: op.feeFromAmount || '', deadline: op.deadline ? op.deadline.slice(0, 10) : '', description: op.description || '', perks: op.perks || [] });
    setCoverImage(null); setCoverPreview(op.imageUrl || null); setDrawerOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const fd = new FormData();
    fd.append('title', form.title); fd.append('country', form.country); fd.append('type', form.type);
    fd.append('feeFromAmount', form.feeFromAmount); fd.append('deadline', form.deadline); fd.append('description', form.description);
    fd.append('perks', JSON.stringify(form.perks));
    if (coverImage) fd.append('image', coverImage);
    await fetch(`${API_BASE}/admin/opportunities${editingId ? `/${editingId}` : ''}`, {
      method: editingId ? 'PATCH' : 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    setSaving(false);
    setDrawerOpen(false);
    setForm(emptyForm); setCoverImage(null); setCoverPreview(null); setEditingId(null);
    fetchOpportunities();
  };

  const handleClose = async (id) => {
    await fetch(`${API_BASE}/admin/opportunities/${id}/close`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchOpportunities();
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AgentSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">My Openings</h2>
            <p className="text-[#64748b] mt-1">Postings you've submitted. New openings require admin approval before going live.</p>
          </div>
          <button
            onClick={openCreate}
            className="text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <span className="material-symbols-outlined">add_circle</span>
            New Opening
          </button>
        </header>

        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-[#f2f4f6]">
              <tr>
                {['Country', 'Title', 'Type', 'Status', ''].map((h) => <th key={h} className="px-6 py-4 text-xs uppercase text-[#64748b]">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {loading ? (
                <tr><td colSpan="5" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
              ) : opportunities.length === 0 ? (
                <tr><td colSpan="5" className="p-12 text-center text-[#64748b]">You haven't posted any openings yet.</td></tr>
              ) : (
                opportunities.map((op) => (
                  <tr key={op.id} className="hover:bg-[#f7f9fb] transition-colors">
                    <td className="px-6 py-4 font-medium text-[#00255a]">{op.country}</td>
                    <td className="px-6 py-4 font-semibold text-[#0f172a]">{op.title}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${op.type === 'STUDY' ? 'bg-[#365da3]/10 text-[#365da3]' : 'bg-[#79161e]/10 text-[#862026]'}`}>{op.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[op.status]}`}>{op.status.replace('_', ' ')}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {op.status === 'OPEN' && (
                        <button onClick={() => handleClose(op.id)} className="text-[#64748b] hover:text-[#00255a] text-sm font-semibold">Close</button>
                      )}
                      <button onClick={() => openEdit(op)} className="ml-4 text-[#64748b] hover:text-[#00255a] text-sm font-semibold">Edit</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {drawerOpen && (
          <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDrawerOpen(false)}>
            <div className="w-full max-w-lg bg-white flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#e2e8f0] flex justify-between items-center">
                <h3 className="text-xl font-bold text-[#00255a]">{editingId ? 'Edit Opening' : 'New Opening'}</h3>
                <button onClick={() => setDrawerOpen(false)} className="p-2 hover:bg-[#f2f4f6] rounded-full">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-8 space-y-6">
                <Field label="Position Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><label className="block text-xs font-bold text-[#44474f] uppercase">Country</label><CountrySelect required value={form.country} onChange={(v) => setForm({ ...form, country: v })} /></div>
                  <div>
                    <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Type</label>
                    <div className="flex p-1 bg-[#f2f4f6] rounded-xl border border-[#e2e8f0]">
                      {['STUDY', 'JOB'].map((t) => (
                        <button key={t} onClick={() => setForm({ ...form, type: t })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${form.type === t ? 'bg-white shadow-sm text-[#00255a]' : 'text-[#64748b]'}`}>
                          {t === 'STUDY' ? 'Study' : 'Job'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Fee (USD)" type="number" value={form.feeFromAmount} onChange={(v) => setForm({ ...form, feeFromAmount: v })} />
                  <Field label="Deadline" type="date" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">Description</label>
                  <textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] outline-none resize-none" />
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-[#44474f] uppercase">Opening Picture</label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-xl bg-[#f2f4f6] border border-[#e2e8f0] overflow-hidden flex items-center justify-center shrink-0">
                      {coverPreview ? <img src={coverPreview} alt="Opening preview" className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-[#64748b]">image</span>}
                    </div>
                    <input type="file" accept="image/*" onChange={(e) => setCoverImage(e.target.files[0] || null)} className="text-sm" />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="block text-xs font-bold text-[#44474f] uppercase">Perks</label>
                  {PERK_OPTIONS.map((perk) => (
                    <label key={perk} className="flex items-center gap-3 p-3 bg-[#f2f4f6] rounded-xl border border-[#e2e8f0] cursor-pointer">
                      <input type="checkbox" checked={form.perks.includes(perk)} onChange={() => togglePerk(perk)} className="w-5 h-5" />
                      <span className="flex-1">{perk}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="p-6 border-t border-[#e2e8f0] flex gap-4">
                <button onClick={() => { setDrawerOpen(false); setCoverImage(null); setCoverPreview(null); setEditingId(null); }} className="flex-1 py-4 border-2 border-[#00255a] text-[#00255a] font-semibold rounded-xl">Discard</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-4 text-white font-semibold rounded-xl shadow-lg disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
                >
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Submit for Approval'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] outline-none" />
    </div>
  );
}
